import type {
  Session,
  SupabaseClient,
} from "@supabase/supabase-js";

import { createClient } from "@supabase/supabase-js";

import { CONFIG } from "@/config-global";

// ----------------------------------------------------------------------

const SUPABASE_UNAVAILABLE_MSG =
  "Supabase is not configured. Add VITE_PUBLIC_SUPABASE_URL and VITE_PUBLIC_SUPABASE_ANON_KEY to use sign-in and the dashboard.";

const supabaseUrl = (CONFIG.supabase.url ?? "").trim();
const supabaseKey = (CONFIG.supabase.key ?? "").trim();

const wantsSupabase = CONFIG.auth.method === "supabase";

/** False when Supabase env vars are missing — the app still loads so public/marketing routes work */
export const isSupabaseConfigured =
  wantsSupabase && Boolean(supabaseUrl && supabaseKey);

// ----------------------------------------------------------------------

function createUnavailableSupabaseStub(): SupabaseClient<any, "public", any> {
  const authErr = (): any =>
    ({
      message: SUPABASE_UNAVAILABLE_MSG,
      status: 400,
      name: "AuthError",
    });

  const noopSub = { unsubscribe: () => {} };

  const authStub = {
    getSession: async () => ({
      data: { session: null as Session | null },
      error: null,
    }),
    getUser: async () => ({
      data: { user: null },
      error: null as any,
    }),
    onAuthStateChange: (
      callback: Parameters<
        SupabaseClient["auth"]["onAuthStateChange"]
      >[0]
    ) => {
      queueMicrotask(() => {
        callback("INITIAL_SESSION", null as any);
      });
      return {
        data: { subscription: noopSub },
      };
    },
    signOut: async () => ({ error: null }),
    signUp: async () => ({
      data: { user: null, session: null },
      error: authErr(),
    }),
    signInWithPassword: async () => ({
      data: { user: null, session: null },
      error: authErr(),
    }),
    signInWithOAuth: async () => ({
      data: { provider: null, url: null },
      error: authErr(),
    }),
    resetPasswordForEmail: async () => ({
      error: authErr(),
    }),
    updateUser: async () => ({
      data: { user: null },
      error: authErr(),
    }),
    resend: async () => ({
      error: authErr(),
    }),
    mfa: {
      enroll: async () => ({ data: null, error: authErr() }),
      challenge: async () => ({ data: null, error: authErr() }),
      verify: async () => ({ data: null, error: authErr() }),
      unenroll: async () => ({ data: null, error: authErr() }),
    },
  };

  const client = {
    // Partial stub: real SupabaseAuthClient has ~100 members; narrow cast avoids unsafe direct overlap assertion.
    auth: authStub as unknown as SupabaseClient["auth"],
    storage: {
      from: () => ({
        upload: async () => ({
          data: null,
          error: { message: SUPABASE_UNAVAILABLE_MSG } as any,
        }),
        getPublicUrl: () => ({
          data: { publicUrl: "" },
        }),
      }),
    },
    rpc: async () =>
      ({
        data: null,
        error: { message: SUPABASE_UNAVAILABLE_MSG },
      }) as any,
  };

  return client as unknown as SupabaseClient<any, "public", any>;
}

export const supabase: SupabaseClient<any, "public", any> = isSupabaseConfigured
  ? createClient(supabaseUrl, supabaseKey)
  : wantsSupabase
    ? createUnavailableSupabaseStub()
    : ({} as SupabaseClient<any, "public", any>);

/** Storage/auth helpers — require a real configured client */
export const isSupabase = isSupabaseConfigured;

// ----------------------------------------------------------------------

// Buckets
export const PRIVATE_BUCKET = "lint-private";
export const PUBLIC_BUCKET = "lint";

type UploadOptions = {
  cacheControl?: string;
  upsert?: boolean;
  contentType?: string;
};

export async function storageUploadFile(
  bucket: string,
  path: string,
  file: File | Blob | ArrayBuffer,
  options?: UploadOptions
) {
  if (!isSupabase) throw new Error("Supabase is not enabled");
  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(path, file as any, options);
  if (error) throw error;
  return data;
}

export function storageGetPublicUrl(
  bucket: string,
  path: string,
  options?: {
    transform?: {
      width?: number;
      height?: number;
      resize?: "cover" | "contain" | "fill";
      quality?: number;
      format?: "origin" | "webp" | "avif";
    };
  }
) {
  if (!isSupabase) throw new Error("Supabase is not enabled");
  const { data } = supabase.storage.from(bucket).getPublicUrl(path, options as any);
  return data.publicUrl;
}
